import {
  Box,
  Heading,
  VStack,
  Text,
  Grid,
  Divider,
  Button,
  StackDivider,
  Flex,
  Spinner,
  TableContainer, Table, Thead, Tr, Th, Tbody, Td, HStack, Icon
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { getAbstractedUserTiers, getAbstractedUserTierTable, getSelectedTierTable } from '@views/User/utils';
import React, { useCallback, useState } from 'react';
import { getUserTiers } from '@store/actions/kycActions';
import ContentLayout from '@layouts/ContentLayout';
import {
  MdOutlineKeyboardArrowDown,
  MdOutlineKeyboardArrowUp,
  MdOutlineKeyboardDoubleArrowLeft,
  MdSearch,
  MdVisibility
} from 'react-icons/md';
import useDebounce from '@hooks/useDebounce';
import useDidMountEffect from '@hooks/useDidMount';
import InputComponent from '@components/Input';
import CopyableText from '@components/CopyableText';
import { formatDate } from '@utils/dateUtils';
import { ProgressBar } from '@components/ProgressBar';
import { USER_KYC_TIER_STATUS } from '@constants';
import DetailsTable from '@components/DetailsTable';
import { updateUserKycTiersReqData } from '@store/actions/reqDataActions';


const UserKycTiers = () => {
  const dispatch  = useDispatch();

  const {data: tierResponse, isLoading, isError, error } = useSelector((state) => state.kyc.user.kycTiers);
  const { userId } = useSelector((state) => state.reqData.kyc.kycTiers);

  const [showRightSidebar, setShowRightSidebar] = useState(true);
  const [searchParams, setSearchParams] = useState({
    userId: userId || ""
  });
  const [selectedTier, setSelectedTier] = useState(null);
  const [isSelectedTierDetailCollapsed, setIsSelectedTierDetailCollapsed] =
    useState(false);

  const debouncedSearchParams = useDebounce(searchParams, 500);

  // Formatting the tier response
  const tiersData = getAbstractedUserTiers(tierResponse);

  const {
    tiers: tiersList,
  } = tiersData || {};

  const triggerGetUserKycTiers = useCallback(
    ({ userId }) => {
      dispatch(
        getUserTiers({
          userId
        })
      );
    }, [dispatch]);

  useDidMountEffect(() => {
    triggerGetUserKycTiers({ userId: searchParams.userId });
  }, [debouncedSearchParams]);

  const handleResize =  useCallback((e) => {
    e?.preventDefault();
    if (showRightSidebar) {
      setShowRightSidebar(false);
    } else {
      setShowRightSidebar(true);
    }
  }, [showRightSidebar]);

  const handleSearchReqChange = useCallback(
    (e) => {
      if (e.target.value === searchParams.userId)
        return;

      setSearchParams((prev) => ({
        ...prev,
        userId: e.target.value
      }));

      dispatch(
        updateUserKycTiersReqData({
          userId: e.target.value
        })
      )
    },
    [dispatch, searchParams.userId]
  );

  const handleViewTierDetails = (e, tier) => {
    setShowRightSidebar(true);
    setSelectedTier(tier);
  }

  const tierItemObj = getSelectedTierTable(selectedTier);
  const userKycTierOverviewObj = getAbstractedUserTierTable(tiersData);

  return (
    <ContentLayout>
      <Grid
        gridTemplateColumns={{
          base: '1fr',
          lg: `${showRightSidebar ? 7 : 10}fr 0fr ${showRightSidebar ? 3 : 0}fr`
        }}
      >
        {/* Main Column */}
        <VStack
          w={'100%'}
          overflowY={'hidden'}
          overflowX={'hidden'}
          h={{ lg: '90vh' }}
          pt={10}
          pb={4}
          px={{ base: 4, sm: 6, lg: 10 }}
          alignItems={'stretch'}
          gap={4}
          divider={<StackDivider borderColor="whiteAlpha.300" />}
        >
          <Heading size="lg" color="colorPrimary">
            User Tier Dashboard
          </Heading>
          <InputComponent
            type="text"
            value={searchParams?.userId}
            name="search"
            handleChange={handleSearchReqChange}
            placeholder="Search User Tiers by User Id...."
            inputTitle="Search User's Tiers"
            leftElement={<MdSearch size="2rem" color="white" />}
            width="40rem"
            maxWidth="100%"
            autoComplete={'off'}
            spellcheck={false}
            autoCorrect={'off'}
          />

          <VStack
            w="100%"
            gap={2}
          >
            { isLoading ? (
              <Flex justifyContent="center" alignItems="center" w="full" h="full">
                <Spinner thickness="4px" size="lg" color="colorPrimary" />
              </Flex>
            ) : isError ? (
              <Box p={4} borderRadius="xl" bg={'backGround'} borderWidth="1px" borderColor={'whiteAlpha.300'}>
                <Text color="white" fontSize="md">
                  {error}
                </Text>
              </Box>
            ) : tiersData ? (
              <VStack
                w={'100%'}
                alignItems={'stretch'}
                gap={4}
              >
                <VStack
                  justifyContent={'start'}
                  alignItems={'stretch'}
                >
                  <Heading size={'md'} color={'colorPrimary'}>
                    User Tier Overview
                  </Heading>
                  {userKycTierOverviewObj.map(
                    (item, index) =>
                      item.data && (
                        <DetailsTable
                          key={index}
                          details={item.data}
                          rowGap={'1rem'}
                          marginTop="0"
                          isSidebar={'true'}
                        />
                      )
                  )}
                </VStack>
                <VStack
                  justifyContent={'start'}
                  alignItems={'stretch'}
                >
                  <Heading size={'md'} color="colorPrimary">
                    User Tiers Table
                  </Heading>
                  <TableContainer
                    border="2px solid"
                    borderColor="whiteAlpha.500"
                    borderRadius="xl"
                    my={4}
                    overflow="visible"
                  >
                    <Table variant="unstyled">
                      <Thead>
                        <Tr
                          bg="backGround"
                          borderBottom="2px solid"
                          borderColor="whiteAlpha.500"
                        >
                          <Th textAlign="center" whiteSpace="nowrap">
                            Tier ID
                          </Th>
                          <Th textAlign="center" whiteSpace="nowrap">
                            Status
                          </Th>
                          <Th textAlign="center" whiteSpace="nowrap">
                            Created At
                          </Th>
                          <Th textAlign="center" whiteSpace="nowrap">
                            Updated At
                          </Th>
                          <Th textAlign="center" whiteSpace="nowrap">
                            Progress
                          </Th>
                          <Th textAlign="center" whiteSpace="nowrap">
                            Action(s)
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {tiersList.map((tier) => (
                          <Tr
                            key={tier.tierId}
                            bg={
                              selectedTier?.tierId === tier.tierId
                                ? '#1a2e1a'
                                : '#1c1c1c'
                            }
                            border="3px solid black"
                            _hover={{ bg: '#111111' }}
                            position="relative"
                            zIndex={
                              selectedTier?.tierId === tier.tierId ? 1 : 0
                            }
                            transition="all 0.3s ease"
                          >
                            <Td textAlign="center" whiteSpace="nowrap">
                              <CopyableText
                                title="Client ID"
                                fontSize="xs"
                                color="white"
                              >
                                {tier.tierId ?? '-'}
                              </CopyableText>
                            </Td>
                            <Td textAlign="center" whiteSpace="nowrap">
                              <HStack justifyContent={'center'}>
                                <Icon viewBox="0 0 200 200" color={tier.tierStatusColor || 'white'} w="1rem">
                                  <path
                                    fill="currentColor"
                                    d="M 100, 100 m -75, 0 a 75,75 0 1,0 150,0 a 75,75 0 1,0 -150,0"
                                  />
                                </Icon>
                                <Text fontSize="xs">
                                  {tier.tierStatus}
                                </Text>
                              </HStack>
                            </Td>
                            <Td textAlign="center" whiteSpace="nowrap">
                              <Text fontSize="xs">
                                {tier.createdAt ? formatDate(tier.createdAt) : '-'}
                              </Text>
                            </Td>
                            <Td textAlign="center" whiteSpace="nowrap">
                              <Text fontSize="xs">
                                {tier.updatedAt ? formatDate(tier.updatedAt) : '-'}
                              </Text>
                            </Td>
                            <Td textAlign="center" whiteSpace="nowrap">
                              {tier.tierStatus === USER_KYC_TIER_STATUS.ACTIVE ? (
                                <ProgressBar
                                  current={tier.consumed || 0}
                                  total={tier.limit || 0}
                                />
                              ) : (
                                <Text fontSize="xs">
                                  Inactive Tier
                                </Text>
                              )}
                            </Td>
                            <Td textAlign="center" whiteSpace="nowrap">
                              <Button
                                colorScheme="brand"
                                size="xs"
                                bg="black"
                                border="1px solid"
                                borderColor="whiteAlpha.300"
                                borderRadius="lg"
                                px={2}
                                leftIcon={<MdVisibility size="1rem" />}
                                onClick={(e) => handleViewTierDetails(e, tier)}
                              >
                                View Details
                              </Button>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </VStack>
              </VStack>
            ) : (
              <Box
                w={'100%'}
                p={4}
                bg="backGround"
                border="1px solid"
                borderColor="whiteAlpha.300"
                borderRadius="xl"
              >
                <Text>
                  {searchParams?.userId
                    ? "This user doesn't have any tiers yet."
                    : "Oops! Looks like you forgot to enter a User ID."}
                </Text>
              </Box>
            )}
          </VStack>
        </VStack>

        <Box alignItems="center" position="relative">
          <Divider
            style={{ margin: '0' }}
            borderColor="whiteAlpha.500"
            orientation="vertical"
            transition="width 0.3s ease-in-out"
          />
          <Button
            colorScheme="brand"
            size="sm"
            bg="black"
            border="2px solid"
            borderColor="whiteAlpha.500"
            borderRadius="xl"
            p={0}
            onClick={(e) => handleResize(e)}
            position={'absolute'}
            top={12}
            left={-4}
          >
            <Box
              as="span"
              transition="transform 0.3s ease-in-out"
              transform={!showRightSidebar ? 'rotate(0deg)' : 'rotate(180deg)'}
              display="inline-block"
            >
              <MdOutlineKeyboardDoubleArrowLeft />
            </Box>
          </Button>
        </Box>

        {/*Right Column*/}
        <VStack
          overflowX={'hidden'}
          h={{ lg: '90vh' }}
          pt={10}
          pb={4}
          px={{ base: 4, sm: 6, lg: 10 }}
          alignItems={'stretch'}
          gap={4}
        >
          {/*NOTE :: We will only display the client details on the sidebar for now.*/}
          {showRightSidebar && !!selectedTier && (
            <Box position="relative" width="100%">
              <VStack
                overflowY={{ base: 'initial', lg: 'scroll' }}
                overflowX="hidden"
                alignItems="stretch"
                gap={4}
              >
                {/*Tier Detail Table*/}
                <VStack
                  w={'100%'}
                  alignSelf={'start'}
                  alignItems={'stretch'}
                  justifyContent={'space-between'}
                  border={'1px solid'}
                  borderColor={'whiteAlpha.300'}
                  bg={'backGround'}
                  borderRadius={'xl'}
                  p={4}
                >
                  <HStack justifyContent="space-between">
                    <Heading fontSize="lg" color="brand.500">
                      Tier Details
                    </Heading>
                    <HStack justifyContent="space-between" gap={2}>
                      {selectedTier?.length > 0 && (
                        <Text fontSize={'xs'} color="brand.500">
                          Tier ID: {selectedTier?.tierId}
                        </Text>
                      )}
                      <Button
                        size="sm"
                        bg={'blackAlpha.600'}
                        border={'1px solid'}
                        borderColor={'whiteAlpha.300'}
                        borderRadius={'xl'}
                        onClick={() =>
                          setIsSelectedTierDetailCollapsed(!isSelectedTierDetailCollapsed)
                        }
                        p={0}
                        variant={'subtle'}
                      >
                        {isSelectedTierDetailCollapsed ? (
                          <MdOutlineKeyboardArrowDown size={'1.25rem'} />
                        ) : (
                          <MdOutlineKeyboardArrowUp size={'1.25rem'} />
                        )}
                      </Button>
                    </HStack>
                  </HStack>
                  {!isSelectedTierDetailCollapsed && (
                    <VStack
                      w="100%"
                      alignSelf="start"
                      alignItems="stretch"
                      justifyContent="space-between"
                      border="1px solid"
                      borderColor="whiteAlpha.300"
                      borderRadius="xl"
                      bg="blackAlpha.600"
                      px={4}
                      py={2}
                    >
                      {tierItemObj.map(
                        (item, index) =>
                          item.data && (
                            <DetailsTable
                              key={index}
                              details={item.data}
                              rowGap={'1rem'}
                              marginTop="0"
                              isSidebar={'true'}
                            />
                          )
                        )
                      }
                    </VStack>
                  )}
                </VStack>
              </VStack>
            </Box>
          )}
        </VStack>
      </Grid>
    </ContentLayout>
  );
};

export default UserKycTiers;